import { numberToHtmlColor } from "@archipad-js/archipad/utils";

import { BugEntity, BugNoteEntity, BugStateEntity, ProjectEntity } from "../../models/EntityClasses";
import { BaseColorContext, ColorFunction, ColorScheme } from "./base-color-context";

/**
 * Returns the color of an observation or a specific  observation's comment at
 * any moment (present, past or future).
 *
 * This is a port of the BugColorContext's Objective-C implementation of
 * Archipad.
 *
 * @see BugColorContext from [Archipad](https://github.com/ArchipadDev/archipad)
 */
export class BugColorContext extends BaseColorContext {
    constructor(
        private readonly project: ProjectEntity,
        originDateTime: Date = new Date(),
        private readonly colorFunction: ColorFunction = BaseColorContext.parseColorFunction(
            project.settingDotColorFunction,
        ),
    ) {
        super(originDateTime);

        if (this.colorFunction === ColorFunction.TimeBased) {
            this.hotDurationInDays = project.settingHotBugDuration ?? 14;
            this.lateAfterEditableCreationDateDurationInDays = project.settingLateBugForVisitDuration ?? 7;
        }
    }

    /**
     * Returns the color of a given observation.
     */
    public modelToColor(bugModel: BugEntity): string {
        if (!bugModel) {
            return ColorScheme.Black;
        }

        if (this.project.isBugWorkflowEnabled) {
            const bugStateModel = bugModel.stateNoteCache?.bugState;
            if (!bugStateModel) {
                return ColorScheme.Black;
            }

            return this._bugWithStateToColor(bugModel, bugStateModel);
        }

        if (bugModel.isClosed) {
            return ColorScheme.Gray;
        }

        return this._getColorUsingColorFunction(bugModel);
    }

    /**
     * Returns the color of a given observation's comment.
     */
    public noteModelToColor(bugNoteModel: BugNoteEntity): string | null {
        const bugStateModel = bugNoteModel?.bugState;
        if (bugStateModel == null) {
            return null;
        }

        return this._bugWithStateToColor(bugNoteModel.bug, bugStateModel);
    }

    private _bugWithStateToColor(bugModel: BugEntity, bugStateModel: BugStateEntity): string {
        if (bugStateModel?.isKindOfClosed) {
            return ColorScheme.Gray;
        }

        // state color
        if (bugStateModel?.color != null) {
            return numberToHtmlColor(bugStateModel.color);
        }

        return this._getColorUsingColorFunction(bugModel);
    }

    private _getColorUsingColorFunction(bugModel: BugEntity): string {
        switch (this.colorFunction) {
            case ColorFunction.TimeBased: {
                return this.getColorUsingDates(bugModel.openDate, bugModel.dueDate);
            }
            case ColorFunction.WorkPackages: {
                return this.getColorUsingWorkPackage(bugModel.workPackage);
            }
            case ColorFunction.Monochrome:
            default: {
                return ColorScheme.Red;
            }
        }
    }
}
